/**
 * Utility geografiche e geometriche.
 *
 * Tutte le operazioni "locali" (ombre, routing) lavorano in una proiezione
 * equirettangolare in metri centrata sull'area di interesse: a scala urbana
 * (< ~10 km) l'errore è trascurabile ed evita dipendenze da librerie GIS.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export interface XY {
  x: number; // metri verso est
  y: number; // metri verso nord
}

export interface Bbox {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
}

const EARTH_RADIUS_M = 6371008.8;
const DEG = Math.PI / 180;

/** Metri per grado di latitudine (circa costante). */
const M_PER_DEG_LAT = 111132.0;

export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = (b.lat - a.lat) * DEG;
  const dLng = (b.lng - a.lng) * DEG;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * DEG) * Math.cos(b.lat * DEG) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function polylineLengthMeters(coords: LatLng[]): number {
  let len = 0;
  for (let i = 1; i < coords.length; i++) len += haversineMeters(coords[i - 1], coords[i]);
  return len;
}

/** Proiezione locale equirettangolare centrata su `origin`. */
export class LocalProjection {
  private readonly kx: number;
  private readonly ky: number;

  constructor(readonly origin: LatLng) {
    this.ky = M_PER_DEG_LAT;
    this.kx = M_PER_DEG_LAT * Math.cos(origin.lat * DEG);
  }

  toXY(p: LatLng): XY {
    return { x: (p.lng - this.origin.lng) * this.kx, y: (p.lat - this.origin.lat) * this.ky };
  }

  toLatLng(p: XY): LatLng {
    return { lat: this.origin.lat + p.y / this.ky, lng: this.origin.lng + p.x / this.kx };
  }
}

export function bboxOf(points: LatLng[]): Bbox {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { minLat, minLng, maxLat, maxLng };
}

/** Espande il bbox di `meters` metri su ogni lato. */
export function expandBbox(b: Bbox, meters: number): Bbox {
  const dLat = meters / M_PER_DEG_LAT;
  const midLat = (b.minLat + b.maxLat) / 2;
  const dLng = meters / (M_PER_DEG_LAT * Math.max(0.15, Math.cos(midLat * DEG)));
  return {
    minLat: b.minLat - dLat,
    minLng: b.minLng - dLng,
    maxLat: b.maxLat + dLat,
    maxLng: b.maxLng + dLng,
  };
}

export function bboxCenter(b: Bbox): LatLng {
  return { lat: (b.minLat + b.maxLat) / 2, lng: (b.minLng + b.maxLng) / 2 };
}

export function bboxContains(b: Bbox, p: LatLng): boolean {
  return p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLng && p.lng <= b.maxLng;
}

/** true se `inner` è interamente contenuto in `outer`. */
export function bboxCovers(outer: Bbox, inner: Bbox): boolean {
  return (
    inner.minLat >= outer.minLat &&
    inner.maxLat <= outer.maxLat &&
    inner.minLng >= outer.minLng &&
    inner.maxLng <= outer.maxLng
  );
}

export function distXY(a: XY, b: XY): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export interface SegProjection {
  /** distanza al quadrato punto-segmento */
  d2: number;
  /** parametro 0..1 lungo il segmento */
  t: number;
  /** punto proiettato */
  point: XY;
}

export function projectPointOnSegment(p: XY, a: XY, b: XY): SegProjection {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = 0;
  if (len2 > 0) {
    t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
  }
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  const ddx = p.x - point.x;
  const ddy = p.y - point.y;
  return { d2: ddx * ddx + ddy * ddy, t, point };
}

/**
 * Douglas–Peucker: restituisce gli indici dei punti mantenuti (estremi inclusi),
 * in ordine crescente.
 */
export function douglasPeuckerIndices(pts: XY[], epsilon: number): number[] {
  if (pts.length <= 2) return pts.map((_, i) => i);
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, pts.length - 1]];
  const eps2 = epsilon * epsilon;
  while (stack.length > 0) {
    const [i0, i1] = stack.pop() as [number, number];
    let maxD2 = -1;
    let maxIdx = -1;
    for (let i = i0 + 1; i < i1; i++) {
      const { d2 } = projectPointOnSegment(pts[i], pts[i0], pts[i1]);
      if (d2 > maxD2) {
        maxD2 = d2;
        maxIdx = i;
      }
    }
    if (maxD2 > eps2 && maxIdx > 0) {
      keep[maxIdx] = 1;
      stack.push([i0, maxIdx], [maxIdx, i1]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < pts.length; i++) if (keep[i]) out.push(i);
  return out;
}

/**
 * Semplifica una polilinea finché i punti *intermedi* mantenuti sono al più
 * `maxInnerPoints` (gli estremi sono sempre mantenuti). Usata per l'export
 * verso Google Maps (limite di 9 waypoint intermedi).
 */
export function simplifyToMaxInnerPoints(pts: XY[], maxInnerPoints: number): number[] {
  if (pts.length - 2 <= maxInnerPoints) return pts.map((_, i) => i);
  let lo = 0.25;
  let hi = 10000;
  let best: number[] | null = null;
  for (let iter = 0; iter < 40; iter++) {
    const mid = Math.sqrt(lo * hi); // ricerca geometrica: epsilon spazia ordini di grandezza
    const idx = douglasPeuckerIndices(pts, mid);
    if (idx.length - 2 <= maxInnerPoints) {
      best = idx;
      hi = mid;
    } else {
      lo = mid;
    }
    if (hi / lo < 1.02) break;
  }
  if (best) return best;
  // fallback: campionamento uniforme
  const out = [0];
  for (let k = 1; k <= maxInnerPoints; k++) {
    out.push(Math.round((k * (pts.length - 1)) / (maxInnerPoints + 1)));
  }
  out.push(pts.length - 1);
  return [...new Set(out)].sort((a, b) => a - b);
}

/** Interpola punti lungo una polilinea XY ogni `stepM` metri (centri dei sotto-tratti). */
export function samplePolylineXY(pts: XY[], stepM: number): XY[] {
  const samples: XY[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const segLen = distXY(a, b);
    if (segLen === 0) continue;
    const n = Math.max(1, Math.round(segLen / stepM));
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      samples.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  if (samples.length === 0 && pts.length > 0) samples.push(pts[0]);
  return samples;
}

export function formatDistance(m: number): string {
  if (m < 950) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 9500 ? 2 : 1)} km`;
}

export function formatDuration(seconds: number): string {
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${min % 60} min`;
}
